import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaTimes } from 'react-icons/fa';
import './GallerySection.css';

// Create a properly typed icon component
const CloseIcon = FaTimes as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const GallerySection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Preload images
  useEffect(() => {
    const loadImages = async () => {
      const imagePromises = galleryItems.map(item => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = item.thumbnail;
          img.onload = resolve;
          img.onerror = reject;
        });
      });

      try {
        await Promise.all(imagePromises);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading images:', error);
        setIsLoading(false);
      }
    };

    loadImages();
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  const galleryItems = [
    {
      id: 1,
      type: 'image',
      src: '/images/gallery/1.jpeg',
      caption: 'Coding Workshop',
      thumbnail: '/images/gallery/1.jpeg'
    },
    {
      id: 2,
      type: 'image',
      src: '/images/gallery/2.jpeg',
      caption: 'Robotics Session',
      thumbnail: '/images/gallery/2.jpeg'
    },
    {
      id: 3,
      type: 'image',
      src: '/images/gallery/3.jpeg',
      caption: 'Electronics Lab',
      thumbnail: '/images/gallery/3.jpeg'
    },
    {
      id: 4,
      type: 'image',
      src: '/images/gallery/4.jpeg',
      caption: '3D Printing',
      thumbnail: '/images/gallery/4.jpeg'
    },
    {
      id: 5,
      type: 'image',
      src: '/images/gallery/5.jpeg',
      caption: 'Team Project',
      thumbnail: '/images/gallery/5.jpeg'
    },
    {
      id: 6,
      type: 'image',
      src: '/images/gallery/6.jpeg',
      caption: 'Coding Challenge',
      thumbnail: '/images/gallery/6.jpeg'
    },
    {
      id: 7,
      type: 'image',
      src: '/images/gallery/7.jpeg',
      caption: 'Workshop',
      thumbnail: '/images/gallery/7.jpeg'
    },
    {
      id: 8,
      type: 'image',
      src: '/images/gallery/8.jpeg',
      caption: 'Presentation',
      thumbnail: '/images/gallery/8.jpeg'
    },
    {
      id: 9,
      type: 'image',
      src: '/images/gallery/9.jpeg',
      caption: 'Graduation',
      thumbnail: '/images/gallery/9.jpeg'
    }
  ];

  const openImageModal = (src: string) => {
    setSelectedImage(src);
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const validGalleryItems = galleryItems.filter(item => item && item.src && item.thumbnail);

  return (
    <section className="gallery-section" id="gallery">
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }} ref={ref}>
        {/* Gallery header removed as it's now in the parent component */}

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <motion.div
            className="gallery-grid"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            {validGalleryItems.map((item) => (
              <motion.div
                key={item.id}
                className="gallery-item"
                variants={itemVariants}
                onClick={() => openImageModal(item.src)}
              >
                <div className="gallery-item-inner">
                  <img 
                    src={item.thumbnail} 
                    alt={item.caption}
                    loading="lazy"
                  />
                  <div className="gallery-caption">
                    <p>{item.caption}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={closeImageModal}>
          <div className="modal-close" onClick={closeImageModal}>
            <CloseIcon />
          </div>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt="Gallery preview" style={{ maxWidth: '90vw', maxHeight: '90vh' }} />
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
